import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { TraceCode, TraceCodeState } from '../../types/traceCode';
import traceCodeApiService from '../../services/traceCodeApiService';

// Helper function to create a blank trace code with default values
export const createBlankTraceCode = (): TraceCode => {
  const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD

  return {
    id: uuidv4(),
    fillingId: '',
    fillingName: '',
    productCode: '',
    productionDate: today,
    batchRecordId: '',
    batchLotNumber: '',
    batchProductionDate: '',
    quantityProduced: null, // Nullable for auto-created entries
    packagedBy: null, // Nullable for auto-created entries
    deliveryLogId: null,
    deliveryDate: null,
    customerDestination: '',
    bakingProductionLogId: null, // Links to baking_production_log
    panQuantityTotalPieces: 0,
    hasPans: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
};

// Async Thunks

export const fetchTraceCodes = createAsyncThunk(
  'traceCodes/fetchTraceCodes',
  async () => {
    return await traceCodeApiService.fetchTraceCodes();
  }
);

export const createTraceCodeAsync = createAsyncThunk(
  'traceCodes/createTraceCode',
  async (traceCode: TraceCode) => {
    return await traceCodeApiService.createTraceCode(traceCode);
  }
);

export const updateTraceCodeAsync = createAsyncThunk(
  'traceCodes/updateTraceCode',
  async (traceCode: TraceCode) => {
    return await traceCodeApiService.updateTraceCode(traceCode);
  }
);

export const deleteTraceCodeAsync = createAsyncThunk(
  'traceCodes/deleteTraceCode',
  async (id: string) => {
    await traceCodeApiService.deleteTraceCode(id);
    return id;
  }
);

// Initial State
const initialState: TraceCodeState = {
  traceCodes: [],
  loading: false,
  error: null,
};

// Slice
const traceCodesSlice = createSlice({
  name: 'traceCodes',
  initialState,
  reducers: {
    // Synchronous reducers
    clearError: (state) => {
      state.error = null;
    },
    addTraceCode: (state, action: PayloadAction<TraceCode>) => {
      state.traceCodes.push(action.payload);
    },
    removeTraceCode: (state, action: PayloadAction<string>) => {
      state.traceCodes = state.traceCodes.filter((code) => code.id !== action.payload);
    },
  },
  extraReducers: (builder) => {
    // Fetch Trace Codes
    builder
      .addCase(fetchTraceCodes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTraceCodes.fulfilled, (state, action: PayloadAction<TraceCode[]>) => {
        state.loading = false;
        state.traceCodes = action.payload;
      })
      .addCase(fetchTraceCodes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch trace codes';
      });

    // Create Trace Code
    builder
      .addCase(createTraceCodeAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTraceCodeAsync.fulfilled, (state, action: PayloadAction<TraceCode>) => {
        state.loading = false;
        state.traceCodes.push(action.payload);
      })
      .addCase(createTraceCodeAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create trace code';
      });

    // Update Trace Code
    builder
      .addCase(updateTraceCodeAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTraceCodeAsync.fulfilled, (state, action: PayloadAction<TraceCode>) => {
        state.loading = false;
        const index = state.traceCodes.findIndex((code) => code.id === action.payload.id);
        if (index !== -1) {
          state.traceCodes[index] = action.payload;
        }
      })
      .addCase(updateTraceCodeAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update trace code';
      });

    // Delete Trace Code
    builder
      .addCase(deleteTraceCodeAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTraceCodeAsync.fulfilled, (state, action: PayloadAction<string>) => {
        state.loading = false;
        state.traceCodes = state.traceCodes.filter((code) => code.id !== action.payload);
      })
      .addCase(deleteTraceCodeAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete trace code';
      });
  },
});

export const { clearError, addTraceCode, removeTraceCode } = traceCodesSlice.actions;
export default traceCodesSlice.reducer;
